import { z } from 'zod';
/**
 * Contrats de la COMPRESSION STRICTEMENT SANS PERTE de la sélection.
 * Source unique de vérité front + back.
 *
 * L'action « Compresser la sélection » ré-optimise les IMAGES (JPEG via jpegtran
 * `-optimize -copy all`, PNG via optipng `-o2`) — SANS AUCUNE perte de pixels (mêmes
 * pixels, mêmes métadonnées) ; seuls les OCTETS (donc `file_hash` et `size_bytes`)
 * changent. L'`identity_hash` (empreinte pixels) reste IDENTIQUE → aucune
 * ré-indexation, aucun faux doublon, vignettes (clées sur identity_hash) inchangées.
 * Les VIDÉOS sont EXCLUES (un ré-encodage lossless est en général plus gros → inutile).
 * Le fichier n'est REMPLACÉ que si le résultat est réellement plus petit.
 *
 * L'écriture sur /library est gardée par `LIBRARY_WRITABLE` (409 WRITE_DISABLED sinon,
 * comme le déplacement / la dédup / l'EXIF). La compression s'exécute en job asynchrone
 * (suivi dans la cloche). Une sauvegarde `_original` est OPTIONNELLE (choix utilisateur).
 *
 * ⚠ PIÈGE ZOD v4 (boot crash) : ce schéma de REQUÊTE passe par
 * `z.toJSONSchema(schema, { target: 'draft-7' })` au démarrage (enregistrement des
 * routes Fastify). Un `.transform()` PERSONNALISÉ y LÈVE → l'API plante en boucle. On
 * n'en met AUCUN (uniquement `.array`/`.boolean`/`.optional`/`.default`/`.refine` —
 * représentables). Garde anti-régression : `route-json-schema.test.ts`.
 */
// ---------------------------------------------------------------------------
// Compression de la sélection (POST /api/libraries/:id/compress)
// ---------------------------------------------------------------------------
/**
 * Corps de `POST /api/libraries/:id/compress`. DEUX modes de cible exclusifs (au
 * moins un requis, sinon 400 via `.refine`) :
 *  - `all: true`             → compresse TOUTES les images de la bibliothèque.
 *  - `assetIds: [...]` (≥ 1) → compresse cette sélection (les non-images sont ignorées).
 *
 * `keepOriginal` (défaut `false`) : garde une sauvegarde `<nom>_original` avant écriture.
 * Défaut décoché : la compression étant SANS PERTE, l'utilisateur veut gagner de la place.
 * `dryRun` (défaut `false`) : APERÇU (compte le gain potentiel sans rien écrire).
 */
export const compressRequestSchema = z
    .object({
    assetIds: z.array(z.uuid()).optional(),
    all: z.boolean().optional(),
    keepOriginal: z.boolean().optional().default(false),
    dryRun: z.boolean().optional().default(false),
})
    .refine((v) => v.all === true || (v.assetIds !== undefined && v.assetIds.length > 0), {
    message: 'Fournir all:true ou une liste assetIds non vide',
});
/**
 * Ventilation d'un job `compress`. Chaque asset ciblé tombe dans une catégorie :
 *  - `processed`   : assets réellement traités (un outil de compression a tourné).
 *  - `compressed`  : fichiers RÉELLEMENT remplacés par une version plus petite
 *                    (en dry-run : ceux qui l'auraient été).
 *  - `skipped`     : non-image (vidéo/format non géré) OU aucun gain (résultat ≥ original).
 *  - `bytesSaved`  : total des octets économisés (somme `avant - après`).
 *  - `failed`      : erreur de l'outil (spawn/parse) ou chemin refusé — non compressé.
 */
export const compressResultSchema = z.object({
    total: z.number().int().nonnegative(),
    processed: z.number().int().nonnegative(),
    compressed: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    bytesSaved: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
//# sourceMappingURL=compress.js.map